import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bookmark, ExternalLink, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { getJson, mediaUrl } from "@/lib/api";
import { postJson, useAuth } from "@/lib/auth";
import { locDoc, useLang } from "@/lib/i18n";
import { ShareMenu } from "@/components/community/PostActions";
import { AR, ES, GB, MX, US } from "country-flag-icons/react/3x2";

const FLAGS = { AR, ES, GB, MX, US };

/** Small SVG flag of the outlet's country (works on every OS, unlike emoji flags). */
export const SourceFlag = ({ country, testid }) => {
  const { lang } = useLang();
  const Flag = FLAGS[country];
  if (!Flag) return null;
  const name = new Intl.DisplayNames([lang], { type: "region" }).of(country);
  return <Flag title={name} aria-label={name} className="inline-block h-[11px] w-auto rounded-[2px] align-[-1px] shadow-[0_0_0_1px_rgba(255,255,255,0.15)]" data-testid={testid} />;
};

export const useNewsDate = () => {
  const { lang } = useLang();
  return (iso) => (iso ? new Date(iso).toLocaleDateString(lang, { day: "numeric", month: "long", year: "numeric" }) : "");
};

/** Saved state for news (shared favorites list with the wall). */
export const useNewsSaved = () => {
  const { t } = useLang();
  const { member } = useAuth();
  const navigate = useNavigate();
  const [saved, setSaved] = useState([]);
  useEffect(() => {
    if (member) getJson("/community/me/state").then((d) => setSaved(Array.isArray(d?.saved) ? d.saved : []));
    else setSaved([]);
  }, [member]);
  const toggle = async (id) => {
    if (!member) return toast(t("news.loginToSave"), { action: { label: t("auth.login"), onClick: () => navigate(`/entrar?next=${encodeURIComponent(`/noticias/${id}`)}`) } });
    try {
      const r = await postJson(`/news/${id}/save`, null);
      setSaved((s) => (r.saved ? [...s, id] : s.filter((x) => x !== id)));
      toast.success(t(r.saved ? "save.added" : "save.removed"));
    } catch (e) {
      toast.error(e.message);
    }
    return undefined;
  };
  return { saved, toggle };
};

export const NewsActions = ({ n, saved, onSave, showSource = true }) => {
  const { t } = useLang();
  const url = `${window.location.origin}/noticias/${n.id}`;
  return (
    <div className="flex items-center gap-1" data-testid={`news-actions-${n.id}`}>
      <Link to={`/noticias/${n.id}#comentarios`} className="post-act" aria-label={t("news.comment")} title={t("news.comment")} data-testid={`news-comment-${n.id}`}>
        <MessageCircle size={15} /> <span>{n.comment_count ?? 0}</span>
      </Link>
      <button type="button" onClick={() => onSave(n.id)} aria-pressed={saved} aria-label={t(saved ? "save.saved" : "save.save")} title={t(saved ? "save.saved" : "save.save")} className={`post-act ${saved ? "text-[var(--green)]" : ""}`} data-testid={`news-save-${n.id}`}>
        <Bookmark size={15} fill={saved ? "currentColor" : "none"} />
      </button>
      <ShareMenu post={n} url={url} text={n.title} testid={`news-share-${n.id}`} />
      {showSource && (
        <a href={n.url} target="_blank" rel="noopener noreferrer" className="post-act ml-auto" title={t("news.readAt", { source: n.source })} data-testid={`news-source-link-${n.id}`}>
          <ExternalLink size={14} /> <span className="mono text-[11px]">{n.source}</span>
        </a>
      )}
    </div>
  );
};

export const NewsCard = ({ n: raw, saved, onSave, featured = false }) => {
  const { lang } = useLang();
  const date = useNewsDate();
  const n = locDoc(raw, lang);
  return (
    <article className={`news-card ${featured ? "news-card-featured" : ""}`} data-testid={`news-card-${n.id}`}>
      <Link to={`/noticias/${n.id}`} className="news-card-media" data-testid={`news-open-${n.id}`}>
        {n.image_url ? <img src={mediaUrl(n.image_url)} alt="" loading="lazy" /> : <span className="news-card-noimg font-display">TMNT</span>}
        <span className="news-card-credit mono">© {n.source}</span>
      </Link>
      <div className="news-card-body">
        <p className="mono text-[11px] uppercase tracking-[0.16em] text-[var(--green)]"><SourceFlag country={n.country} testid={`news-flag-${n.id}`} /> <span data-testid={`news-source-${n.id}`}>{n.source}</span> <span className="text-[var(--muted)]">· {date(n.published_at)}</span></p>
        <h2 className={`font-display mt-2 leading-tight ${featured ? "text-3xl md:text-4xl" : "text-2xl"}`}>
          <Link to={`/noticias/${n.id}`} className="hover:text-[var(--green)]" data-testid={`news-title-${n.id}`}>{n.title}</Link>
        </h2>
        <p className={`mt-3 text-sm leading-relaxed text-[var(--muted)] ${featured ? "line-clamp-4" : "line-clamp-3"}`} data-testid={`news-summary-${n.id}`}>{n.summary}</p>
        <div className="mt-auto pt-4"><NewsActions n={n} saved={saved} onSave={onSave} /></div>
      </div>
    </article>
  );
};
